'use client';

import { c as _c } from "react/compiler-runtime";
import { type PropsWithChildren, createContext, useContext, useEffect, useRef } from 'react';
import { getFaviconHref, getTitle, setFaviconHref, setTitle } from '@/components/HtmlHead/utils';
export type FocusHandler = () => void;
export type BlurHandler = () => FocusHandler | undefined;
export interface HtmlHeadState {
  getIsBlurred: () => boolean;
  getOriginalFaviconHref: () => string;
  getOriginalTitle: () => string;
  registerBlurHandler: (blurHandler: BlurHandler) => void;
  unregisterBlurHandler: (blurHandler: BlurHandler) => void;
}
const HtmlHeadContext = createContext<HtmlHeadState>(undefined as unknown as HtmlHeadState);
export function HtmlHeadProvider(t0) {
  const $ = _c(13);
  const {
    children
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = [];
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const blurHandlersRef = useRef(t1);
  const isBlurredRef = useRef(false);
  const faviconHrefRef = useRef("");
  const titleRef = useRef("");
  let t2;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = () => isBlurredRef.current;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  const getIsBlurred = t2;
  let t3;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = () => faviconHrefRef.current;
    $[2] = t3;
  } else {
    t3 = $[2];
  }
  const getOriginalFaviconHref = t3;
  let t4;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = () => titleRef.current;
    $[3] = t4;
  } else {
    t4 = $[3];
  }
  const getOriginalTitle = t4;
  let t5;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = blurHandler => {
      blurHandlersRef.current.push(blurHandler);
    };
    $[4] = t5;
  } else {
    t5 = $[4];
  }
  const registerBlurHandler = t5;
  let t6;
  if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = blurHandler_0 => {
      blurHandlersRef.current = blurHandlersRef.current.filter(handler => handler !== blurHandler_0);
    };
    $[5] = t6;
  } else {
    t6 = $[5];
  }
  const unregisterBlurHandler = t6;
  let t7;
  let t8;
  if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = () => {
      faviconHrefRef.current = getFaviconHref();
      titleRef.current = getTitle();
      const mutationObserver = new MutationObserver(() => {
        if (isBlurredRef.current) {
          return;
        }
        faviconHrefRef.current = getFaviconHref();
        titleRef.current = getTitle();
      });
      mutationObserver.observe(document.head, {
        subtree: true,
        childList: true,
        attributes: true,
        attributeFilter: ["href"]
      });
      return () => {
        mutationObserver.disconnect();
      };
    };
    t8 = [];
    $[6] = t7;
    $[7] = t8;
  } else {
    t7 = $[6];
    t8 = $[7];
  }
  useEffect(t7, t8);
  let t10;
  let t9;
  if ($[8] === Symbol.for("react.memo_cache_sentinel")) {
    t9 = () => {
      const focusHandlers = [];
      const handleBlur = () => {
        if (isBlurredRef.current) {
          return;
        }
        isBlurredRef.current = true;
        for (const blurHandler_1 of blurHandlersRef.current) {
          const focusHandler = blurHandler_1();
          if (focusHandler) {
            focusHandlers.push(focusHandler);
          }
        }
      };
      const handleFocus = () => {
        if (!isBlurredRef.current) {
          return;
        }
        isBlurredRef.current = false;
        while (focusHandlers.length) {
          focusHandlers.pop()?.();
        }
        if (titleRef.current) {
          setTitle(titleRef.current);
        }
        if (faviconHrefRef.current) {
          setFaviconHref(faviconHrefRef.current);
        }
      };
      const handleVisibilityChange = () => {
        if (document.hidden) {
          handleBlur();
        } else {
          handleFocus();
        }
      };
      document.addEventListener("visibilitychange", handleVisibilityChange);
      window.addEventListener("blur", handleBlur);
      window.addEventListener("focus", handleFocus);
      return () => {
        document.removeEventListener("visibilitychange", handleVisibilityChange);
        window.removeEventListener("blur", handleBlur);
        window.removeEventListener("focus", handleFocus);
      };
    };
    t10 = [];
    $[8] = t10;
    $[9] = t9;
  } else {
    t10 = $[8];
    t9 = $[9];
  }
  useEffect(t9, t10);
  let t11;
  if ($[10] === Symbol.for("react.memo_cache_sentinel")) {
    t11 = {
      getIsBlurred,
      getOriginalFaviconHref,
      getOriginalTitle,
      registerBlurHandler,
      unregisterBlurHandler
    };
    $[10] = t11;
  } else {
    t11 = $[10];
  }
  let t12;
  if ($[11] !== children) {
    t12 = <HtmlHeadContext.Provider value={t11}>{children}</HtmlHeadContext.Provider>;
    $[11] = children;
    $[12] = t12;
  } else {
    t12 = $[12];
  }
  return t12;
}
export function useHtmlHeadState() {
  const context = useContext(HtmlHeadContext);
  if (!context) {
    throw new Error("useHtmlHeadState must be used within a HtmlHeadContext");
  }
  return context;
}